import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { accountService } from "~/services/contabilidad/accountService";
import type { AccountFilters, AccountFormData } from "~/types/contabilidad";

export const ACCOUNTS_QUERY_KEY = ["accounts"];

export function useAccounts(filters?: AccountFilters) {
  return useQuery({
    queryKey: [...ACCOUNTS_QUERY_KEY, filters],
    queryFn: () => accountService.getAll(filters),
  });
}

export function useAccountsByCompany(companyId: string) {
  return useQuery({
    queryKey: [...ACCOUNTS_QUERY_KEY, "company", companyId],
    queryFn: () => accountService.getByCompany(companyId),
    enabled: !!companyId,
  });
}

export function useAccount(id: string) {
  return useQuery({
    queryKey: [...ACCOUNTS_QUERY_KEY, id],
    queryFn: () => accountService.getById(id),
    enabled: !!id,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AccountFormData) => accountService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: [...ACCOUNTS_QUERY_KEY, "company", data.company_id],
      });
      toast.success("Cuenta creada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear cuenta: ${error.message}`);
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<AccountFormData>;
    }) => accountService.update(id, data),
    onSuccess: (updatedAccount, { id }) => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...ACCOUNTS_QUERY_KEY, id] });
      queryClient.invalidateQueries({
        queryKey: [...ACCOUNTS_QUERY_KEY, "company", updatedAccount.company_id],
      });
      toast.success("Cuenta actualizada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar cuenta: ${error.message}`);
    },
  });
}

export function useToggleAccountActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      accountService.toggleActive(id, isActive),
    onSuccess: (updatedAccount, { id }) => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...ACCOUNTS_QUERY_KEY, id] });
      queryClient.invalidateQueries({
        queryKey: [...ACCOUNTS_QUERY_KEY, "company", updatedAccount.company_id],
      });
      toast.success(
        updatedAccount.is_active ? "Cuenta activada" : "Cuenta desactivada"
      );
    },
    onError: (error: Error) => {
      toast.error(`Error al cambiar estado de cuenta: ${error.message}`);
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => accountService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
      toast.success("Cuenta eliminada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar cuenta: ${error.message}`);
    },
  });
}
